<template>
	<div class="col-12">
		<div class="card card-xl-stretch mb-xl-8">
			<div class="card-header border-0">
				<h3 class="card-title fw-bolder text-dark">
					<span class="fa-solid fa-rotate me-3"></span>
					<span>Sincronização de Prova</span>
				</h3>
			</div>
			<div class="card-body">
				<div class="row align-items-end">
					<div class="col-12 col-md-4">
						<label for="cpf" class="form-label">
							Cpf do aluno
						</label>
						<input
							v-model="cpf"
							v-maska
							data-maska="###.###.###-##"
							type="text"
							class="form-control"
							id="cpf"
							placeholder="000.000.000-00" />
					</div>
					<div class="col-12 col-md-3 mt-5 mt-md-0">
						<div class="d-grid gap-2">
							<button
								@click="syncExamProdesp"
								:disabled="!cpf_filled || loading"
								class="btn btn-primary"
								type="button">
								<span v-if="loading">
									<i class="fa fa-spinner fa-spin"></i>
									Carregando
								</span>
								<span v-else>Sincronizar</span>
							</button>
						</div>
					</div>
				</div>
				<div v-if="response_success || response_error" class="row mt-5">
					<div class="col-12 col-md-6">
						<div
							v-if="response_success && response_obj"
							class="alert fw-bold"
							:class="alert_class"
							role="alert">
							<p>{{ result_message }}</p>
							<ul>
								<li>
									Matrícula:
									{{ response_obj.schedule.enrollment_id }}
								</li>
								<li>
									Código da prova:
									{{
										response_obj.schedule.enrollment_test_id
									}}
								</li>
								<li>
									Data da prova:
									{{ response_obj.schedule.schedule_date }}
								</li>
								<li>
									Situação:
									{{ response_obj.schedule.state }}
								</li>
								<li>
									Computador:
									{{ response_obj.schedule.computer }}
								</li>
								<li>
									CFC:
									{{ response_obj.schedule.subtenant }}
								</li>
								<li>
									<a
										:href="
											response_obj.schedule.schedule_url
										"
										target="_blank">
										Ver agendamento
									</a>
								</li>
							</ul>
						</div>
						<div
							v-if="response_error"
							class="alert alert-danger fw-bold"
							role="alert">
							<p>Erro ao sincronizar !</p>
							<p>{{ error_message }}</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
	import axios from 'axios';
	import { vMaska } from 'maska';
	import { computed, ref } from 'vue';

	interface Schedule {
		enrollment_test_id: number;
		enrollment_id: number;
		schedule_date: string;
		state: string;
		computer: string | null;
		subtenant: string;
		subtenant_id: number;
		schedule_url: string;
	}

	interface Response {
		schedule: Schedule;
		action: string;
		already_had_exam_scheduled: boolean;
	}

	/**
	 * Cada desfecho descreve o que a sincronização de fato fez, para que um
	 * no-op não seja lido como correção aplicada.
	 */
	const ACTION_MESSAGES: Record<string, string> = {
		created: 'Sincronização realizada com sucesso ! Prova criada a partir do agendamento da Prodesp.',
		reopened:
			'Sincronização realizada com sucesso ! A prova estava cancelada no nosso sistema e foi reaberta.',
		already_scheduled:
			'Nada a sincronizar. O aluno já possui prova agendada.',
		reopened_outdated:
			'Sincronização realizada com sucesso ! A prova estava cancelada no nosso sistema e foi reaberta. Atenção: a data da prova já passou — cancele o agendamento manualmente se for o caso.',
	};

	const props = defineProps({
		sync_exam_prodesp_route: { type: String, required: true },
	});

	const cpf = ref('');
	const cpf_filled = computed(() => cpf.value.length === 14);
	const loading = ref(false);
	const error_message = ref('');
	const response_error = ref(false);
	const response_success = ref(false);
	const response_obj = ref<Response | null>(null);

	const result_message = computed(
		() =>
			ACTION_MESSAGES[response_obj.value?.action ?? ''] ??
			'Sincronização realizada com sucesso !'
	);

	const alert_class = computed(() =>
		response_obj.value?.action === 'reopened_outdated'
			? 'alert-warning'
			: 'alert-success'
	);

	async function syncExamProdesp() {
		loading.value = true;
		response_error.value = false;
		response_success.value = false;
		try {
			const response = await axios.post(props.sync_exam_prodesp_route, {
				cpf: cpf.value,
			});
			response_obj.value = response.data;
			response_success.value = true;
		} catch (error: any) {
			error_message.value = error.response.data.message;
			response_error.value = true;
		} finally {
			loading.value = false;
		}
	}
</script>
